import { defineStore } from 'pinia';
import { useErrorHandling } from 'src/core/composables/useErrorHandling';
import { useApiStore } from 'src/core/stores/api';
import { EnumType, FormatedEnum } from 'src/core/types/enum';
import { ResponseExportEnums } from 'src/core/types/enum/export';

type ExportEnumsStore = {
  enums: ResponseExportEnums;
  formatedEnums: FormatedEnum[];
};

/**
 * A store for managing export enums data.
 */
export const useExportEnumsStore = defineStore('exportEnums', {
  state: () =>
    ({
      enums: {} as ResponseExportEnums,
      formatedEnums: [] as FormatedEnum[],
    } as ExportEnumsStore),

  getters: {
    /**
     * Get the formatted value of an enum.
     * @param enumVal - The enum value to format.
     * @returns The formatted enum value.
     */
    getFormatedEnumValue: (state) => (enumVal: string) => {
      return (
        state.formatedEnums.find(
          (enumValue: FormatedEnum) => enumValue[enumVal]
        )?.[enumVal] || enumVal
      );
    },

    /**
     * Get the enum instance based on the enum value.
     * @param enumVal - The enum value to search for.
     * @returns The enum value if found, undefined otherwise.
     */
    getEnumInstance: (state) => (enumVal: string) => {
      let foundEnum: (EnumType & { format?: string }) | undefined;
      Object.keys(state.enums).forEach((key: string) => {
        if (foundEnum) return;
        foundEnum = state.enums[key as keyof typeof state.enums].find(
          (enumValue) => enumValue.value === enumVal
        );
      });
      return foundEnum;
    },
  },

  actions: {
    /**
     * Get enums information.
     */
    async getEnums() {
      const apiStore = useApiStore();
      const { handleError } = useErrorHandling();
      try {
        const responseEnums = await apiStore.service.get<ResponseExportEnums>(
          '/export/enums'
        );
        this.formatedEnums = [];

        this.enums = responseEnums.data;
        Object.keys(responseEnums.data).forEach((key: string) => {
          responseEnums.data[key as keyof ResponseExportEnums].forEach(
            (enumValue: EnumType) => {
              this.formatedEnums.push({ [enumValue.value]: enumValue.label });
            }
          );
        });
      } catch (error) {
        handleError('Enums error', error);
      }
    },
  },
});
